/**
 * Connects the {@link LoanEditDrawer} to persistence. Owns a `useDealSettings`
 * instance for the property being edited and writes the strategy-blob patch the
 * drawer emits back to `property_setting`.
 *
 * Kept always-mounted by the page; `item` going null closes the drawer. The
 * last item is retained through the close animation so the panel doesn't flash
 * empty on the way out.
 */
import { useEffect, useState } from "react";

import { useDealLoans, type FinancingInput } from "@/hooks/use-deal-loans";
import { useDealSettings } from "@/hooks/use-deal-settings";
import type { OwnedProperty } from "@/hooks/use-owned-properties";

import { LoanEditDrawer, type FinancingSavePatch } from "./LoanEditDrawer";
import { resolveArv, type FinancingSettings } from "./financing-metrics";

interface FinancingEditorProps {
  /** Property under edit, or null when the drawer is closed. */
  item: OwnedProperty | null;
  onClose: () => void;
  /** Called after a successful save so the page can refetch. */
  onSaved: () => void;
}

export function FinancingEditor({ item, onClose, onSaved }: FinancingEditorProps) {
  // Retain the last item through the close animation.
  const [shown, setShown] = useState<OwnedProperty | null>(item);
  useEffect(() => {
    if (item) setShown(item);
  }, [item]);

  const { settings, saveSettings } = useDealSettings(shown?.id);
  const { financingRow, saveFinancing } = useDealLoans(shown?.id);

  const purchase = Number(settings?.purchase_price) || shown?.purchasePrice || 0;
  const arv = resolveArv(purchase, settings?.after_repair_value);

  const handleSave = async (
    patch: FinancingSavePatch | null,
    financing: FinancingInput | null,
  ) => {
    // Loan terms → property_setting; lender / enabled / slot → loans table.
    // The drawer passes null for whichever table is unchanged, so we skip its
    // write and avoid no-op PATCHes.
    const ops: Promise<unknown>[] = [];
    if (patch) ops.push(saveSettings.mutateAsync(patch));
    if (financing) ops.push(saveFinancing.mutateAsync(financing));
    if (ops.length === 0) return;
    await Promise.all(ops);
    onSaved();
  };

  return (
    <LoanEditDrawer
      open={item !== null}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      propertyAddress={shown?.property.address ?? "Property"}
      strategy={shown?.strategy ?? "BRRRR"}
      purchase={purchase}
      arv={arv}
      settings={settings as FinancingSettings}
      loan={financingRow}
      saving={saveSettings.isPending || saveFinancing.isPending}
      onSave={handleSave}
    />
  );
}
